import {
  CaretDownIcon,
  CheckCircleIcon,
  InfoIcon,
  ScalesIcon,
  SealCheckIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { evidenceLine, severityKey, translatableExplanations } from './explanations';
import styles from './MatchInsights.module.css';
import type { CompromiseSeverity, ExplanationItem } from './types';
import { useExplanationContext } from './useExplanationContext';

/**
 * `neutral` — факты без знака (MATCHING §9): «автомобиль в соседней земле» не
 * довод за него и не компромисс. Отдельная группа, а не подмешивание к
 * преимуществам: список, названный «Преимущества», обязан содержать только их.
 */
export type ExplanationKind = 'why' | 'advantages' | 'neutral' | 'compromises';

interface ExplanationGroupProps {
  kind: ExplanationKind;
  items: readonly ExplanationItem[];
  currency: string;
}

const ICONS = {
  why: CheckCircleIcon,
  advantages: SealCheckIcon,
  neutral: InfoIcon,
  compromises: ScalesIcon,
} as const;

const SEVERITY_CLASS: Record<CompromiseSeverity, string | undefined> = {
  high: styles.severityHigh,
  medium: styles.severityMedium,
  low: styles.severityLow,
};

/**
 * Список выводов подбора с раскрытием основания (FE-083, MVP_PRD §18).
 *
 * Раскрытие — не украшение: продукт требует, чтобы каждая причина показывала,
 * **какой критерий пользователя** использован, **какое значение автомобиля**
 * взято и **какой расчёт** выполнен. Поэтому каждый пункт разворачивается в
 * список оснований, а вывод без оснований не показывается вовсе.
 *
 * Раскрытие сделано нативным `<details>`: клавиатура, скринридеры и поиск по
 * странице работают без единой строки собственной логики фокуса.
 */
export function ExplanationGroup({ kind, items, currency }: ExplanationGroupProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const context = useExplanationContext(currency);
  const Icon = ICONS[kind];

  const visible = translatableExplanations(items, context);
  if (visible.length === 0) return null;

  return (
    <section className={styles.group} aria-labelledby={titleId}>
      <h3 id={titleId} className={styles.groupTitle}>
        <Icon size={18} weight="bold" aria-hidden="true" />
        {t(`results.detail.${kind}.title`)}
      </h3>

      <ul className={styles.items}>
        {visible.map(({ item, text }) => {
          const lines = item.evidence.map((entry) => evidenceLine(entry, context));
          const severityClass =
            item.severity === undefined ? undefined : SEVERITY_CLASS[item.severity];

          return (
            <li key={item.code} className={styles.item}>
              <details className={styles.details}>
                <summary className={styles.itemSummary}>
                  <span className={styles.summaryText}>
                    {text}
                    {item.severity !== undefined ? (
                      <span className={[styles.severity, severityClass].filter(Boolean).join(' ')}>
                        {kind === 'compromises' ? (
                          <WarningIcon size={12} weight="fill" aria-hidden="true" />
                        ) : null}
                        {t('results.detail.severity', {
                          value: t(severityKey(item.severity)),
                        })}
                      </span>
                    ) : null}
                  </span>
                  <span className={styles.summaryIcon} aria-hidden="true">
                    <CaretDownIcon size={14} weight="bold" />
                  </span>
                  <span className="visually-hidden">{t('results.detail.expand')}</span>
                </summary>

                <dl className={styles.evidence}>
                  {lines.map((line, position) => (
                    <div key={`${line.kind}-${String(position)}`} className={styles.evidenceRow}>
                      <dt>
                        <span className={styles.evidenceKind}>
                          {t(`results.detail.evidence.${line.kind}`)}
                        </span>
                        {line.label}
                      </dt>
                      <dd className="tabular">{line.value || t('catalog.noData')}</dd>
                    </div>
                  ))}
                </dl>
              </details>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
